import { toast } from "react-toastify";

const showErrorToast = (message, { critical = false } = {}) => {
  toast.error(message, {
    position: "top-center",
    autoClose: critical ? false : 4000,
    draggable: !critical,
    icon: "🚨",
    className: critical ? "critical-toast" : "",
    theme: "light",
  });
};

/**
 * Recursively flatten an error detail object/array into
 * [ pathString, messageString ] tuples.
 */
function flattenErrorDetail(detail, prefix = "") {
  const results = [];

  if (typeof detail === "string") {
    results.push([prefix, detail]);
  } else if (Array.isArray(detail)) {
    detail.forEach((item, idx) => {
      const path = prefix ? `${prefix}[${idx}]` : `[${idx}]`;
      results.push(...flattenErrorDetail(item, path));
    });
  } else if (typeof detail === "object" && detail !== null) {
    Object.entries(detail).forEach(([key, val]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      results.push(...flattenErrorDetail(val, path));
    });
  }

  return results;
}

/**
 * 🚨 Main Error Handler
 */
export const handleApiError = (error, { critical = false } = {}) => {
  // 1) Plain object literals thrown directly from client-side validation code
  // (e.g. handleApiError({ Error: "Order not loaded" })) — these have no
  // `.response` because they were never sent to the server, but they are NOT
  // network failures. They're real Error/AxiosError instances that get the
  // network-error treatment below; plain objects get their fields shown.
  if (error && typeof error === "object" && !(error instanceof Error) && !error.response) {
    Object.entries(error).forEach(([field, msg]) => {
      showErrorToast(`${msg}`, { critical });
    });
    return;
  }

  // 2) No response at all => genuine network failure, timeout, CORS, DNS,
  // server unreachable. This only reaches here for actual Error/AxiosError
  // instances now, not plain validation objects.
  if (!error?.response) {
    const message = error?.message || "Network error — please check your connection and try again.";
    showErrorToast(message, { critical: true });
    return;
  }

  // 2) HTTP errors with data
  if (error.response && error.response.data) {
    const { data: errorData, status } = error.response;

    // Only treat auth/permission/server failures as "critical" (sticky, manual
    // dismiss). Routine 400 validation errors auto-dismiss like normal toasts.
    const isCritical = critical || status === 401 || status === 403 || status >= 500;

    // a) error_code + error_detail schema
    if (errorData.error_code && errorData.error_detail) {
      const { error_code, error_detail } = errorData;

      // Session expired / invalid token -> one friendly message, not the raw
      // backend detail (which varies: expired, malformed, blacklisted, etc.)
      if (error_code === "AUTH_ERROR") {
        showErrorToast("Your session has expired. Please log in again.", {
          critical: true,
        });
        return;
      }

      // SPECIAL CASE: DRF-style "items" array validation
      if (error_detail.items && Array.isArray(error_detail.items)) {
        error_detail.items.forEach((item) => {
          if (item && typeof item === "object") {
            Object.entries(item).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                messages.forEach((msg) => {
                  showErrorToast(
                    `${field.toUpperCase()}: ${msg}`,
                    { critical: isCritical }
                  );
                });
              } else {
                showErrorToast(
                  `${field.toUpperCase()}: ${messages}`,
                  { critical: isCritical }
                );
              }
            });
          }
        });
        return;
      }

      // Fallback flattening
      const flat = flattenErrorDetail(error_detail);

      if (flat.length) {
        flat.forEach(([path, message]) => {
          showErrorToast(`${message}`, { critical: isCritical });
        });
      } else if (typeof error_detail === "string") {
        showErrorToast(error_detail, { critical: isCritical });
      }

      return;
    }

    // b) generic field-level validation errors
    if (typeof errorData === "object") {
      const flat = flattenErrorDetail(errorData);

      flat.forEach(([path, message]) => {
        showErrorToast(`${path}: ${message}`, { critical: false }); // validation = non-critical
      });

      return;
    }
  }

  // 3) fallback (always critical)
  showErrorToast("SRVR_ERROR: An unexpected error occurred.", {
    critical: true,
  });
};

/**
 * ✅ Success handler (unchanged but slightly improved)
 */
export const handleApiSuccess = (res) => {
  if (typeof res === "string") {
    toast.success(res, {
      position: "top-right",
      autoClose: 3000,
      theme: "light",
    });
  }
};